/**
 * DEMOLITION RULES — a jurisdiction's "major remodel" rules (docs/bim-plan/28 round 11,
 * database/296). Drafted from research, each with its citation, and confirmed by a person. TEAM
 * REFERENCE ONLY (Matt, 2026-10-11: the firm presents the most favorable defensible reading) —
 * nothing prints a rule or a verdict on a sheet.
 * Shown on a jurisdiction in Permitting, Zones & Jurisdictions.
 */
import { useEffect, useState } from 'react'
import { listDemolitionRules, confirmDemolitionRule } from '../services/developmentStandards.js'
import { alertDialog } from './Dialog.jsx'

const FONT = 'DM Sans, sans-serif'
const COMPONENT_LABEL = { extWalls: 'exterior walls (linear feet, every floor)', roof: 'roof (square feet)', foundation: 'foundation (linear feet)', floor: 'floor structure (square feet)' }

function describe(p) {
  const comps = (p.components || []).map((c) => COMPONENT_LABEL[c] || c)
  const how = p.counting === 'combined'
    ? `The share removed, averaged across ${comps.join(', ')}, at ${p.threshold_pct}% or more.`
    : `Any one of ${comps.join(', ')} at ${p.threshold_pct}% or more removed.`
  const adds = p.additions_pct ? ` Or new floor area of ${p.additions_pct}% or more of the existing.` : ''
  return how + adds
}

export default function DemolitionRulesPanel({ jurisdictionId, byName }) {
  const [rules, setRules] = useState(null)
  const [busy, setBusy] = useState(null)
  useEffect(() => {
    setRules(null)
    if (!jurisdictionId) return
    listDemolitionRules(jurisdictionId).then(setRules).catch(() => setRules([]))
  }, [jurisdictionId])

  async function toggle(r) {
    setBusy(r.id)
    try {
      const row = await confirmDemolitionRule(r.id, !r.confirmed, byName)
      setRules((rs) => rs.map((x) => (x.id === row.id ? row : x)))
    } catch (err) {
      await alertDialog({ title: 'Not saved', message: err.message || 'The rule could not be updated.' })
    }
    setBusy(null)
  }

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 4 }}>Demolition rules</div>
      <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 10, lineHeight: 1.45 }}>
        When a remodel removes enough of the existing building that the city treats it as new construction. Reference for the team: AlderCAD’s demolition sheets print only the measured amounts, never a rule or a verdict, so a project can argue the reading it needs.
      </div>
      {rules === null ? (
        <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading…</div>
      ) : !rules.length ? (
        <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)' }}>No demolition rules on file for this jurisdiction.</div>
      ) : rules.map((r) => {
        const p = r.params || {}
        return (
          <div key={r.id} style={{ border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: 12, marginBottom: 10, background: 'var(--aj-bg-surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, fontFamily: FONT, fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{r.label}</div>
              <span style={{ fontFamily: FONT, fontSize: 11, padding: '2px 8px', borderRadius: 20, background: r.confirmed ? 'rgba(46,125,50,0.12)' : 'rgba(184,100,10,0.14)', color: r.confirmed ? '#2e7d32' : '#b8640a' }}>
                {r.confirmed ? 'Confirmed' : 'Not confirmed'}
              </span>
              <button disabled={busy === r.id} onClick={() => toggle(r)}
                style={{ fontFamily: FONT, fontSize: 12, padding: '4px 12px', borderRadius: 20, cursor: 'pointer', border: '0.5px solid #07272D', background: r.confirmed ? 'transparent' : '#07272D', color: r.confirmed ? 'var(--aj-text-mid)' : '#fff' }}>
                {r.confirmed ? 'Withdraw' : 'Confirm'}
              </button>
            </div>
            <div style={{ fontFamily: FONT, fontSize: 12.5, color: 'var(--aj-text-mid)', marginTop: 6, lineHeight: 1.45 }}>{describe(p)}</div>
            {r.notes ? <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 4, lineHeight: 1.45 }}>{r.notes}</div> : null}
            {(p.not_measured || []).length ? (
              <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4 }}>The model cannot measure: {p.not_measured.join('; ')}.</div>
            ) : null}
            <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 6 }}>{r.citation}</div>
          </div>
        )
      })}
    </div>
  )
}
